<template>
  <div class="container-fluid py-4">
    <!-- Header -->
        <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
              <h6 class="text-white text-capitalize ps-3 mb-0">Dashboard</h6>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading data...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <h5 class="text-danger">Error Loading Data</h5>
            <p class="text-muted">{{ error }}</p>
            <button @click="loadStats" class="btn btn-primary">Retry</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Stats -->
    <div v-else-if="stats" class="row">
      <!-- Users Card -->
      <div class="col-xl-2 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-primary shadow-primary text-center border-radius-xl mt-n4 position-absolute">
              <i class="fas fa-users opacity-10" aria-hidden="true"></i>
            </div>
            <div class="text-end pt-1">
              <p class="text-sm mb-0 text-capitalize">Users</p>
              <h4 class="mb-0">{{ stats.total_users }}</h4>
            </div>
          </div>
          <hr class="dark horizontal my-0">
          <div class="card-footer p-3">
            <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.total_users }}</span> Total Users</p>
          </div>
        </div>
      </div>

      <!-- Leads Card -->
      <div class="col-xl-2 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 position-absolute">
              <i class="fas fa-filter opacity-10" aria-hidden="true"></i>
            </div>
            <div class="text-end pt-1">
              <p class="text-sm mb-0 text-capitalize">Leads</p>
              <h4 class="mb-0">{{ stats.total_leads }}</h4>
            </div>
          </div>
          <hr class="dark horizontal my-0">
          <div class="card-footer p-3">
            <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.total_leads }}</span> Total Leads</p>
          </div>
        </div>
      </div>

      <!-- Properties Card -->
      <div class="col-xl-2 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 position-absolute">
              <i class="fas fa-home opacity-10" aria-hidden="true"></i>
            </div>
            <div class="text-end pt-1">
              <p class="text-sm mb-0 text-capitalize">Properties</p>
              <h4 class="mb-0">{{ stats.total_properties }}</h4>
            </div>
          </div>
          <hr class="dark horizontal my-0">
          <div class="card-footer p-3">
            <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.total_properties }}</span> Total Properties</p>
          </div>
        </div>
      </div>

      <!-- Compounds Card -->
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-warning shadow-warning text-center border-radius-xl mt-n4 position-absolute">
              <i class="fas fa-building opacity-10" aria-hidden="true"></i>
            </div>
            <div class="text-end pt-1">
              <p class="text-sm mb-0 text-capitalize">Compounds</p>
              <h4 class="mb-0">{{ stats.total_compounds }}</h4>
            </div>
          </div>
          <hr class="dark horizontal my-0">
          <div class="card-footer p-3">
            <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.total_compounds }}</span> Total Compounds</p>
          </div>
        </div>
      </div>

      <!-- Developers Card -->
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-dark shadow-dark text-center border-radius-xl mt-n4 position-absolute">
              <i class="fas fa-hard-hat opacity-10" aria-hidden="true"></i>
            </div>
            <div class="text-end pt-1">
              <p class="text-sm mb-0 text-capitalize">Developers</p>
              <h4 class="mb-0">{{ stats.total_developers }}</h4>
            </div>
          </div>
          <hr class="dark horizontal my-0">
          <div class="card-footer p-3">
            <p class="mb-0"><span class="text-success text-sm font-weight-bolder">+{{ stats.total_developers }}</span> Total Developers</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts and Tables -->
    <div v-if="stats" class="row mt-4">
      <!-- Lead Pipeline Chart -->
      <div class="col-lg-6 col-md-12">
        <div class="card z-index-2">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2 bg-transparent">
            <div class="bg-gradient-success shadow-success border-radius-lg py-3 pe-1">
              <div class="chart">
                <canvas id="leads-chart" class="chart-canvas" height="300"></canvas>
              </div>
            </div>
          </div>
          <div class="card-body">
            <h6 class="mb-0">Leads Distribution by Stage</h6>
            <p class="text-sm">Lead stages in the system</p>
            <hr class="dark horizontal">
            <div class="d-flex">
              <i class="material-icons text-sm my-auto me-1">schedule</i>
              <p class="mb-0 text-sm">Updated just now</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Users Table -->
      <div class="col-lg-6 col-md-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="row">
              <div class="col-lg-6 col-7">
                <h6>Recent Users</h6>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pb-2">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Email</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Registration Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in stats.recent_users" :key="user.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ user.name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ user.email }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">{{ formatDate(user.created_at) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="stats" class="row mt-4">
      <!-- Recent Leads Table -->
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="row">
              <div class="col-lg-6 col-7">
                <h6>Recent Leads</h6>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pb-2">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Client Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Stage</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date Added</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="lead in stats.recent_leads" :key="lead.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ lead.client_name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="badge badge-sm" :class="getLeadStageBadgeClass(lead.stage)">{{ lead.stage }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">{{ formatDate(lead.created_at) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import StatsService from '@/services/stats.service';
import Stats from '@/models/Stats';
import permissionsMixin from '@/mixins/permissions';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'Dashboard',
  mixins: [permissionsMixin],
  data() {
    return {
      loading: true,
      error: null,
      stats: null,
      statsService: StatsService,
      leadsChart: null
    };
  },
  computed: {
    canViewStats() {
      return this.isSuperAdmin() || this.checkAnyPermission([
        'view stats', 'view-stats', 'view dashboard', 'view-dashboard'
      ]);
    }
  },
  async created() {
    await this.loadStats();
  },
  mounted() {
    const token = this.$route.query.token;
    if (token) {
      // Store the token in localStorage
      localStorage.setItem('jwt_token', token);

      // Remove the token from the URL for security and cleanliness
      this.$router.replace({ query: null });
    }
  },
  beforeUnmount() {
    if (this.leadsChart) {
      this.leadsChart.destroy();
    }
  },
  methods: {
    async loadStats() {
      try {
        this.loading = true;
        this.error = null;
        
        // Check if user has permission to view stats
        if (!this.canViewStats) {
          console.log('User does not have permission to view stats, redirecting...');
          this.redirectToFirstAvailablePage();
          return;
        }
        
        const response = await this.statsService.getOverview();
        console.log('Stats response:', response);
        
        if (response.data) {
          this.stats = new Stats(response.data);
          this.$nextTick(() => {
            this.initLeadsChart();
          });
        } else {
          throw new Error('No stats data received');
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        
        if (error.response?.status === 403) {
          console.log('403 Forbidden - User does not have view stats permission');
          this.redirectToFirstAvailablePage();
          return;
        }
        
        this.error = error.message || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },

    initLeadsChart() {
      const ctx = document.getElementById('leads-chart');
      if (!ctx || !this.stats?.leads_by_stage) return;

      if (this.leadsChart) {
        this.leadsChart.destroy();
      }

      const data = this.stats.leads_by_stage;
      const labels = Object.keys(data).map(key => key.toUpperCase());
      const values = Object.values(data);

      this.leadsChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Leads Count',
            data: values,
            backgroundColor: 'rgba(255, 255, 255, .8)',
            borderWidth: 0,
            borderRadius: 4,
            borderSkipped: false,
            maxBarThickness: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            }
          },
          interaction: {
            intersect: false,
            mode: 'index',
          },
          scales: {
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
                color: 'rgba(255, 255, 255, .2)'
              },
              ticks: {
                suggestedMin: 0,
                suggestedMax: 500,
                beginAtZero: true,
                padding: 10,
                font: {
                  size: 14,
                  weight: 300,
                  family: "Roboto",
                  style: 'normal',
                  lineHeight: 2
                },
                color: "#fff"
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
                color: 'rgba(255, 255, 255, .2)'
              },
              ticks: {
                display: true,
                color: '#f8f9fa',
                padding: 10,
                font: {
                  size: 14,
                  weight: 300,
                  family: "Roboto",
                  style: 'normal',
                  lineHeight: 2
                },
              }
            },
          },
        },
      });
    },

    formatDate(dateString) {
      if (!dateString) return '---';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    getLeadStageBadgeClass(stage) {
      const classes = {
        cil: 'bg-gradient-info',
        eoi: 'bg-gradient-warning',
        reservation: 'bg-gradient-primary',
        sale_claim: 'bg-gradient-success'
      };
      return classes[stage.toLowerCase()] || 'bg-gradient-secondary';
    },

    getStatusLabel(status) {
      const labels = {
        awaiting_payment: 'Awaiting Payment',
        awaiting_review: 'Awaiting Review',
        paid: 'Paid',
        rejected: 'Rejected'
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        awaiting_payment: 'bg-warning',
        awaiting_review: 'bg-info',
        paid: 'bg-success',
        rejected: 'bg-danger'
      };
      return classes[status] || 'bg-light';
    },


    redirectToFirstAvailablePage() {
      const user = this.$store.getters['auth/currentUser'];
      const permissions = user?.permissions || [];
      
      const permissionRoutes = [
        { permissions: ['view subjects', 'view-subjects', 'create subjects', 'create-subjects', 'edit subjects', 'edit-subjects'], route: 'Subjects' },
        { permissions: ['view courses', 'view-courses', 'create courses', 'create-courses', 'edit courses', 'edit-courses'], route: 'Courses' },
        { permissions: ['view teachers', 'view-teachers', 'create teachers', 'create-teachers', 'edit teachers', 'edit-teachers'], route: 'Teachers' },
        { permissions: ['view questions', 'view-questions', 'create questions', 'create-questions', 'edit questions', 'edit-questions', 'view question options', 'view-question-options', 'create question options', 'create-question-options', 'edit question options', 'edit-question-options', 'view answers', 'view-answers', 'create answers', 'create-answers', 'edit answers', 'edit-answers', 'view homework', 'view-homework', 'create homework', 'create-homework', 'edit homework', 'edit-homework'], route: 'SessionQuestionsList' },
        { permissions: ['view reservations', 'view-reservations', 'create reservations', 'create-reservations', 'edit reservations', 'edit-reservations'], route: 'ReservationsIndex' },
        { permissions: ['view countries', 'view-countries', 'create countries', 'create-countries', 'edit countries', 'edit-countries'], route: 'Countries' }
      ];

      for (const item of permissionRoutes) {
        if (permissions.some(permission => item.permissions.includes(permission))) {
          this.$router.push({ name: item.route });
          return;
        }
      }

      // Fallback to no-access page if no specific permissions found
      this.$router.push({ name: 'NoAccess' });
    }
  }
};
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #0277bd 0%, #29b6f6 100%);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f57c00 0%, #ffb74d 100%);
}

.shadow-primary {
  box-shadow: 0 4px 20px 0 rgba(13, 71, 161, 0.14);
}

.shadow-success {
  box-shadow: 0 4px 20px 0 rgba(46, 125, 50, 0.14);
}

.shadow-info {
  box-shadow: 0 4px 20px 0 rgba(2, 119, 189, 0.14);
}

.shadow-warning {
  box-shadow: 0 4px 20px 0 rgba(245, 124, 0, 0.14);
}

.icon {
  width: 3rem;
  height: 3rem;
}

.icon-lg {
  width: 4rem;
  height: 4rem;
}

.border-radius-xl {
  border-radius: 0.75rem;
}

.text-xxs {
  font-size: 0.75rem;
}

.card {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 0;
}

.card:hover {
  box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}
</style>
